/**
 * User Management Page
 *
 * Complete CRUD interface for managing platform users.
 * Uses UserList presentation component, React Query hooks, and modal forms.
 */

import { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Button,
  ErrorState,
  FilterBar,
  PageLayout,
  PageHeader,
  type Filter,
} from '@lognai/synapse-design-system-v2';
import { useUsers } from '../hooks';
import { UserList } from '../components/users/UserList';
import { UserCreateModal, useUserActions } from '../components/users';
import { useListUrlState } from '../hooks/use-list-url-state';
import { UserStatus } from '../types/domain-enums';

const FILTER_KEYS = ['status'] as const;

const STATUS_OPTIONS = Object.values(UserStatus).map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

export function UserManagementPage() {
  const {
    page,
    limit,
    searchInput,
    search,
    filters,
    hasActiveFilters,
    setPage,
    setLimit,
    setSearchInput,
    setFilter,
    clearFilters,
  } = useListUrlState({ filterKeys: FILTER_KEYS });

  const filter = {
    ...(search && { search }),
    ...(filters.status && { status: filters.status as UserStatus }),
  };

  const {
    data: usersPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUsers(
    { page, limit, ...(Object.keys(filter).length > 0 && { filter }) },
    // Keep the previous page on screen while the next one loads, so the table
    // does not blank out between keystrokes or page changes.
    { placeholderData: keepPreviousData }
  );
  const { actions, dialogs } = useUserActions();
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const userFilters: Filter[] = useMemo(
    () => [
      {
        type: 'search',
        key: 'search',
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search by name, email or employee ID',
      },
      {
        type: 'select',
        key: 'status',
        value: filters.status,
        onChange: (value) => setFilter('status', value),
        options: STATUS_OPTIONS,
        placeholder: 'All Statuses',
      },
    ],
    [searchInput, setSearchInput, filters.status, setFilter]
  );

  return (
    <>
      <PageLayout>
        <PageHeader
          title="User Management"
          subtitle="People with access to your organisation."
          actions={
            <Button onClick={() => setCreateModalOpen(true)}>Create User</Button>
          }
        />

        <FilterBar
          filters={userFilters}
          hasActiveFilters={hasActiveFilters}
          onClear={clearFilters}
        />

        {error ? (
          <ErrorState
            title="Could not load users"
            message={error.message}
            action={
              <Button variant="secondary" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : (
          <UserList
            users={usersPage?.items ?? []}
            // isFetching, not isLoading: with keepPreviousData the query stops
            // being "loading" once cached data exists, but the table should
            // still read as busy while the next page is in flight.
            isLoading={isLoading || isFetching}
            hasActiveFilters={hasActiveFilters}
            {...actions}
            pagination={usersPage?.pagination}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        )}
      </PageLayout>

      <UserCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSuccess={() => {
          // Data will auto-refresh via React Query
        }}
      />

      {dialogs}
    </>
  );
}
